/**
 * Default Estimate & Rendering Calculator configuration (portal editing).
 * Merged with saved userPlugins[pluginId].customizations.estimateCalculator on load.
 */

const OP = ['×', '+', '−', '÷'];

export function getDefaultEstimateCalculatorConfig() {
  return {
    settings: {
      webhookUrl: '',
      webhookNotApplicable: false,
      apiKey: ''
    },
    estimateRangePercent: 15,
    estimateFormulas: {
      kitchenRenovation: {
        label: 'Kitchen Renovation',
        routingRef: "What's the scope of your kitchen project?",
        operators: [...OP]
      },
      bathroomRemodel: {
        label: 'Bathroom Remodel',
        routingRef: "What's the scope of your bathroom project?",
        operators: [...OP]
      },
      basementRenovation: {
        label: 'Basement Renovation',
        routingRef: "What's the scope of your basement project?",
        operators: [...OP]
      },
      roomAddition: {
        label: 'Room Addition',
        routingRef: 'What type of addition are you considering?',
        operators: [...OP]
      },
      wholeHomeRenovation: {
        label: 'Whole-Home Renovation',
        routingRef: "What's the scope of your whole-home project?",
        operators: [...OP]
      }
    },
    estimateReadyPage: {
      estimateTitle: 'Your Estimate is Ready!',
      estimateSubtitle:
        "Here's your personalized budget range for your {project_type} project.",
      reimaginedSectionTitle: 'Your {project_type}, Reimagined',
      yourVisionTitle: 'Your Vision',
      visionSummaryTemplate:
        "You're planning a {project_type} for your kitchen. Timeline: {timeline}.",
      estimatedInvestmentTitle: 'Your estimated investment',
      locationNoteEnabled: true,
      locationNoteText:
        "Note: Your location may be outside our primary service area. We'll reach out to confirm availability.",
      locationInRangeList: '',
      preliminaryEstimateNote:
        'This is a preliminary estimate based on typical projects. Your actual price will be determined during a free in-home consultation.',
      getStartedTitle: 'Ready to Get Started?',
      getStartedSubtitle:
        "We'll contact you within 24 hours to schedule your free in-home consultation.",
      callPhoneDigits: '6154730841',
      scheduleConsultationLink: ''
    },
    question1: {
      headerText: 'What type of project are you planning?',
      options: [
        { id: 'kr', routeKey: 'kitchenRenovation', label: 'Kitchen Renovation', baseCost: 70000 },
        { id: 'br', routeKey: 'bathroomRemodel', label: 'Bathroom Remodel', baseCost: 65000 },
        { id: 'bas', routeKey: 'basementRenovation', label: 'Basement Renovation', baseCost: 80000 },
        { id: 'ra', routeKey: 'roomAddition', label: 'Room Addition', baseCost: 55000 },
        { id: 'wh', routeKey: 'wholeHomeRenovation', label: 'Whole-Home Renovation', baseCost: 80000 }
      ]
    },
    question2: {
      headerText: 'What type of property is this?',
      options: [
        { id: 'sf', label: 'Single-family Home', reject: false },
        { id: 'ct', label: 'Condo / Townhouse', reject: true },
        { id: 'mf', label: 'Multi-family / Duplex', reject: true },
        { id: 'cp', label: 'Commercial Property', reject: true }
      ],
      rejection: {
        header: 'We specialize in single-family homes',
        subheader:
          'Our expertise is specifically focused on single-family home renovations. For condos, multi-family, or commercial properties, we recommend connecting with contractors who specialize in those property types.',
        referralHeader: 'Recommended Resources',
        referrals: [
          { label: 'HomeAdvisor', url: 'https://www.homeadvisor.com/' },
          { label: "Angi (Angie's List)", url: 'https://www.angi.com/' },
          { label: 'Houzz', url: 'https://www.houzz.com/' }
        ],
        contactUsAnywayLink: 'https://',
        getInTouchLink: 'https://'
      }
    },
    question7: {
      headerText: 'When are you hoping to start?',
      options: [
        { id: 'asap', label: 'As soon as possible' },
        { id: '13', label: '1 - 3 months' },
        { id: '36', label: '3 - 6 months' },
        { id: 'explore', label: 'Just exploring options' }
      ]
    },
    question9: {
      headerText: 'What is your zip code or city?',
      placeholderText: 'Enter your zip code or city name'
    },
    routing: {
      kitchenRenovation: {
        routeLabel: 'Kitchen Renovation',
        questions: [
          {
            num: 3,
            questionText: "What's the scope of your kitchen project?",
            options: [
              { label: 'Complete gut renovation', value: 1 },
              { label: 'Cabinet & countertop refresh', value: 0.75 },
              { label: 'Appliance swap only', value: 0.25 }
            ]
          },
          {
            num: 4,
            questionText: 'Approximately how large is your kitchen?',
            options: [
              { label: 'Under 100 sq ft', value: 0.85 },
              { label: '100 – 150 sq ft', value: 1 },
              { label: '150 – 250 sq ft', value: 1.25 },
              { label: '250+ sq ft', value: 1.5 }
            ]
          },
          {
            num: 5,
            questionText: 'Will your kitchen layout change?',
            options: [
              { label: 'Yes, moving walls', value: 1.25 },
              { label: 'Yes, changing layout (same walls)', value: 1.12 },
              { label: 'No, keeping current footprint', value: 1 }
            ]
          },
          {
            num: 6,
            questionText: 'What cabinet quality are you considering?',
            options: [
              { label: 'Stock cabinets (builder grade)', value: 0.85 },
              { label: 'Semi-custom cabinets', value: 1 },
              { label: 'Full custom cabinets', value: 1.25 }
            ]
          }
        ]
      },
      bathroomRemodel: {
        routeLabel: 'Bathroom Remodel',
        questions: [
          {
            num: 3,
            questionText: "What's the scope of your bathroom project?",
            options: [
              { label: 'Full gut remodel', value: 1 },
              { label: 'Tub-to-shower conversion', value: 0.75 },
              { label: 'Fixture update only', value: 0.4 }
            ]
          },
          {
            num: 4,
            questionText: 'Approximately how large is the bathroom?',
            options: [
              { label: 'Under 40 sq ft (powder room)', value: 0.75 },
              { label: '40 – 70 sq ft (standard)', value: 1 },
              { label: '70 – 120 sq ft (large)', value: 1.35 },
              { label: '120+ sq ft (master suite)', value: 1.7 }
            ]
          },
          {
            num: 5,
            questionText: 'Which bathroom(s) are you remodeling?',
            options: [
              { label: 'Master bathroom', value: 1.35 },
              { label: 'Guest / hall bathroom', value: 1 },
              { label: 'Powder room / half bath', value: 0.8 },
              { label: 'Multiple bathrooms', value: 1.85 }
            ]
          },
          {
            num: 6,
            questionText: 'How much tile work do you want?',
            options: [
              { label: 'Full floor + all walls', value: 1.25 },
              { label: 'Floor + shower/tub walls', value: 1 },
              { label: 'Floor only', value: 0.85 }
            ]
          }
        ]
      },
      basementRenovation: {
        routeLabel: 'Basement Renovation',
        questions: [
          {
            num: 3,
            questionText: "What's the scope of your basement project?",
            options: [
              { label: 'Full basement finish', value: 0 },
              { label: 'Partial finish (one area)', value: 0.65 },
              { label: 'Waterproofing only', value: 0 }
            ]
          },
          {
            num: 4,
            questionText: 'What is the approximate size of your basement?',
            options: [
              { label: 'Under 500 sq ft', value: 0.7 },
              { label: '500 – 800 sq ft', value: 0.85 },
              { label: '800 – 1,200 sq ft', value: 1 },
              { label: '1,200+ sq ft', value: 1.2 }
            ]
          },
          {
            num: 5,
            questionText: 'Does your basement have egress windows?',
            options: [
              { label: 'Yes, code-compliant egress', value: 1 },
              { label: 'No, will need to add them', value: 1.1 },
              { label: 'Not sure', value: 1.05 },
              { label: 'N/A - no bedroom planned', value: 1 }
            ]
          },
          {
            num: 6,
            questionText: 'What features do you want in your basement?',
            options: [
              { label: 'Bedroom + bathroom', value: 1.35 },
              { label: 'Entertainment / media area', value: 1.08 },
              { label: 'Home office / flex space', value: 1.05 },
              { label: 'Bar / wet bar area', value: 1.18 }
            ]
          }
        ]
      },
      roomAddition: {
        routeLabel: 'Room Addition',
        questions: [
          {
            num: 3,
            questionText: 'What type of addition are you considering?',
            options: [
              { label: 'New construction addition', value: 1 },
              { label: 'Bump-out expansion', value: 0.7 },
              { label: 'Garage conversion', value: 0.75 },
              { label: 'Sunroom / porch enclosure', value: 0.65 }
            ]
          },
          {
            num: 4,
            questionText: 'What size addition are you considering?',
            options: [
              { label: 'Under 150 sq ft', value: 0.75 },
              { label: '150 – 300 sq ft', value: 1 },
              { label: '300 – 500 sq ft', value: 1.35 },
              { label: '500+ sq ft', value: 1.75 }
            ]
          },
          {
            num: 5,
            questionText: 'Will the addition include plumbing?',
            options: [
              { label: 'Yes, full bathroom', value: 1.25 },
              { label: 'Yes, half bath only', value: 1.15 },
              { label: 'Yes, kitchenette/wet bar', value: 1.2 },
              { label: 'No plumbing needed', value: 1 }
            ]
          },
          {
            num: 6,
            questionText: 'What will the addition be used for?',
            options: [
              { label: 'Master suite (bed + bath)', value: 1.25 },
              { label: 'Bedroom only', value: 1 },
              { label: 'Living / family room', value: 1.1 },
              { label: 'Home office', value: 0.95 }
            ]
          }
        ]
      },
      wholeHomeRenovation: {
        routeLabel: 'Whole-Home Renovation',
        questions: [
          {
            num: 3,
            questionText: "What's the scope of your whole-home project?",
            options: [
              { label: 'Full gut renovation', value: 1.35 },
              { label: 'Major remodel (multiple rooms)', value: 1 },
              { label: 'Cosmetic refresh throughout', value: 0.75 }
            ]
          },
          {
            num: 4,
            questionText: 'What is the total size of your home?',
            options: [
              { label: 'Under 1,500 sq ft', value: 0.8 },
              { label: '1,500 – 2,500 sq ft', value: 1 },
              { label: '2,500 – 3,500 sq ft', value: 1.3 },
              { label: '3,500+ sq ft', value: 1.7 }
            ]
          },
          {
            num: 5,
            questionText: 'Approximately how old is your home?',
            options: [
              { label: 'Under 20 years', value: 1 },
              { label: '20 - 40 years', value: 1.08 },
              { label: '40 - 60 years', value: 1.18 },
              { label: '60+ years', value: 1.3 }
            ]
          },
          {
            num: 6,
            questionText: 'Which areas are included in the renovation?',
            options: [
              { label: 'Kitchen + bathrooms only', value: 0.85 },
              { label: 'Kitchen + baths + living areas', value: 1 },
              { label: 'All rooms + structural changes', value: 1.35 }
            ]
          }
        ]
      }
    },
    question8: {
      headerText: 'Want to see what your space could look like?',
      imageDisclaimerText:
        'AI-generated visualization. Actual results will vary based on final design.'
    },
    question10: {
      title: 'Almost There!',
      buttonText: 'See My Estimate',
      fields: [
        {
          id: 'name',
          label: 'Full Name',
          placeholder: 'Enter your Full Name',
          required: true
        },
        {
          id: 'email',
          label: 'Email Address',
          placeholder: 'Enter your Email',
          required: true
        },
        {
          id: 'phone',
          label: 'Phone Number',
          placeholder: 'Enter your phone number',
          required: true
        }
      ],
      contentCheckboxEnabled: true,
      contentCheckboxText:
        'I agree to be contacted about my project and I understand this estimate is preliminary.'
    }
  };
}

/**
 * Question 1 options are the source of truth for the Q3–Q6 routes.
 * Legacy saved configs have no routeKey on the option, so fall back to the
 * original option id → route key pairing before generating a fresh key.
 */
const LEGACY_OPTION_ID_ROUTE_KEYS = {
  kr: 'kitchenRenovation',
  br: 'bathroomRemodel',
  bas: 'basementRenovation',
  ra: 'roomAddition',
  wh: 'wholeHomeRenovation'
};

function routeKeyFromLabel(label) {
  const words = String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
  if (words.length === 0) return 'route';
  return words
    .map((w, i) => (i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join('');
}

function uniqueRouteKey(base, taken) {
  const root = base || 'route';
  let key = root;
  let n = 2;
  while (taken.has(key)) {
    key = `${root}${n}`;
    n += 1;
  }
  return key;
}

/** Blank Q3–Q6 bundle for a project type that has no route yet. */
export function createRoutingBundle(label) {
  return {
    routeLabel: label,
    questions: [3, 4, 5, 6].map((num) => ({
      num,
      questionText: '',
      options: [{ label: 'New option', value: 1 }]
    }))
  };
}

/**
 * Keeps routing (Q3–Q6) and estimate formulas in step with Question 1:
 * removing a Question 1 option drops its route, renaming one renames its route,
 * and adding one creates a route to fill in. Idempotent — safe to run on every edit.
 */
export function syncRoutingWithQuestion1(config) {
  if (!config || typeof config !== 'object') return config;

  const options = Array.isArray(config.question1?.options)
    ? config.question1.options
    : [];
  const routing =
    config.routing && typeof config.routing === 'object' ? config.routing : {};
  const formulas =
    config.estimateFormulas && typeof config.estimateFormulas === 'object'
      ? config.estimateFormulas
      : {};
  const defaults = getDefaultEstimateCalculatorConfig();

  const keys = new Array(options.length).fill(null);
  const taken = new Set();

  // Pass 1 — options that already carry an explicit route key keep it.
  options.forEach((opt, i) => {
    const key = opt && opt.routeKey;
    if (key && !taken.has(key)) {
      keys[i] = key;
      taken.add(key);
    }
  });

  // Pass 2 — match by current route label, then by legacy option id, else mint one.
  options.forEach((opt, i) => {
    if (keys[i]) return;
    const label = String(opt?.label ?? '').trim();
    let key = Object.keys(routing).find(
      (k) => !taken.has(k) && String(routing[k]?.routeLabel ?? '').trim() === label
    );
    if (!key) {
      const legacy = LEGACY_OPTION_ID_ROUTE_KEYS[opt?.id];
      if (legacy && !taken.has(legacy)) key = legacy;
    }
    if (!key) key = uniqueRouteKey(routeKeyFromLabel(label), taken);
    keys[i] = key;
    taken.add(key);
  });

  const nextOptions = options.map((opt, i) => ({ ...opt, routeKey: keys[i] }));
  const nextRouting = {};
  const nextFormulas = {};

  nextOptions.forEach((opt) => {
    const key = opt.routeKey;
    const label = String(opt.label ?? '');
    const existing = routing[key] || defaults.routing[key];
    const bundle =
      existing && Array.isArray(existing.questions) && existing.questions.length > 0
        ? { ...JSON.parse(JSON.stringify(existing)), routeLabel: label }
        : createRoutingBundle(label);
    nextRouting[key] = bundle;

    const existingFormula = formulas[key] || defaults.estimateFormulas[key];
    nextFormulas[key] = {
      ...(existingFormula || {}),
      label,
      routingRef:
        existingFormula?.routingRef ?? (bundle.questions[0]?.questionText || ''),
      operators:
        Array.isArray(existingFormula?.operators) && existingFormula.operators.length === 4
          ? [...existingFormula.operators]
          : [...OP]
    };
  });

  return {
    ...config,
    question1: { ...config.question1, options: nextOptions },
    routing: nextRouting,
    estimateFormulas: nextFormulas
  };
}

export function deepMergeEstimateConfig(base, saved) {
  if (!saved || typeof saved !== 'object') return JSON.parse(JSON.stringify(base));
  const out = JSON.parse(JSON.stringify(base));
  function merge(a, b) {
    if (!b || typeof b !== 'object') return;
    Object.keys(b).forEach((k) => {
      if (
        b[k] !== null &&
        typeof b[k] === 'object' &&
        !Array.isArray(b[k]) &&
        a[k] &&
        typeof a[k] === 'object' &&
        !Array.isArray(a[k])
      ) {
        merge(a[k], b[k]);
      } else {
        a[k] = b[k];
      }
    });
  }
  merge(out, saved);
  return out;
}
